import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios'
import { TokenSetParameters } from '@/auth/session/TokenSet'
import { analytics } from '@/layout/Analytics/Analytics'
import { getRID } from '@/utils/RIDUtil'
import { logger } from '@/utils/logging'

export const MINIMUM_PASSWORD_LENGTH = 8
const CLIENT_ID = 'angel_web'

export type CheckUserPayload = {
  exists?: boolean
  errors?: string[]
}

type AcceptDocumentRequest = {
  document_ids: string[]
}

export class AuthApi {
  api: AxiosInstance

  constructor() {
    this.api = axios.create()
    this.setApiHeaders()
  }

  getBaseUrl = () => {
    const baseUrl = process.env.NEXT_PUBLIC_ELLIS_BACKEND_URL
    if (!baseUrl) {
      throw new Error('NEXT_PUBLIC_ELLIS_BACKEND_URL is not set.')
    }
    return baseUrl
  }

  setApiHeaders = (locale = 'en', region = 'en-US') => {
    const {
      user: { anonymousId },
    } = analytics.getState()
    this.api.defaults.headers.common = {
      'X-Accept-Language': locale,
      'X-Region': region,
      // TODO: I don't know if X-Region-Override is needed for Ellis or not. Hold off until we know. Adding to federated graph caused CORS errors for some reason.
      // 'X-Region-Override': region,
      'X-RID': getRID(),
      'X-DID': anonymousId,
      'x-segment-anon-id': anonymousId,
      'x-platform': 'angel-web',
      'Content-Type': 'application/x-www-form-urlencoded',
    }
  }

  createUser = async (
    userEmail: string,
    password: string,
    verificationCode: string,
    dateOfBirth?: string,
    sex?: string,
    firstName?: string,
    lastName?: string,
    documentIds?: string[],
  ): Promise<AxiosResponse<TokenSetParameters> | undefined> => {
    const baseURL = this.getBaseUrl()

    try {
      interface CreateUserPayload {
        email: string
        password: string
        client_id: string
        verification_code: string
        date_of_birth?: string
        sex?: string
        first_name?: string
        last_name?: string
        document_ids?: string[]
      }
      const payload: CreateUserPayload = {
        email: userEmail,
        password,
        client_id: CLIENT_ID,
        verification_code: verificationCode,
      }
      if (dateOfBirth) payload.date_of_birth = dateOfBirth
      if (sex) payload.sex = sex
      if (firstName) payload.first_name = firstName
      if (lastName) payload.last_name = lastName
      if (documentIds && documentIds.length > 0) payload.document_ids = documentIds
      const authResponse = await this.api.post<TokenSetParameters>('/signup', payload, { baseURL })
      return authResponse
    } catch (err) {
      if (axios.isAxiosError<TokenSetParameters>(err)) {
        return err.response
      }
      throw err
    }
  }

  checkUser = async (userEmail: string): Promise<CheckUserPayload> => {
    const baseURL = this.getBaseUrl()
    if (!process.env.BIM_HEADER) {
      const errorMessage = `BIM_HEADER is not set. checkUser has accidentally been called client side.`
      logger().error(errorMessage, { userEmail })
      return { errors: [errorMessage] }
    }

    this.api.defaults.headers.common['x-bim'] = process.env.BIM_HEADER

    // TODO Jon says there may be a better API call for check user. Need to follow-up.
    const authResponse = await this.api
      .get(`/originals/user?email=${encodeURIComponent(userEmail)}`, { baseURL })
      .catch((error) => {
        logger().error('Error checking user', { userEmail }, error)
        return error.response
      })

    if (authResponse.status < 300) {
      return authResponse.data
    } else {
      return { errors: authResponse.data.errors }
    }
  }

  getAuthToken = async (
    userEmail: string,
    password: string,
  ): Promise<AxiosResponse<TokenSetParameters> | undefined> => {
    const baseURL = this.getBaseUrl()

    try {
      const authResponse = await this.api.post<TokenSetParameters>(
        '/oauth/token',
        {
          grant_type: 'password',
          client_id: CLIENT_ID,
          username: userEmail,
          password: password,
        },
        { baseURL },
      )
      return authResponse
    } catch (err) {
      if (axios.isAxiosError<TokenSetParameters>(err)) {
        return err.response
      }
      throw err
    }
  }

  acceptDocuments = async (documentIds: string[], authorization: string): Promise<AxiosResponse | AxiosError> => {
    const baseURL = this.getBaseUrl()
    this.api.defaults.headers.common['Authorization'] = authorization

    const request: AcceptDocumentRequest = {
      document_ids: documentIds,
    }

    return this.api
      .post('/documents/accept', request, { baseURL })
      .then((response) => {
        logger().info('Successfully accepted legal documents for a newly created user.', { documentIds })
        return response
      })
      .catch((e) => {
        logger().error('Failed to accept legal documents for a newly created user', { documentIds }, e)

        if (axios.isAxiosError(e)) {
          return e
        }
        throw e
      })
  }

  sendVerificationCode = async (email: string): Promise<AxiosResponse | AxiosError['response']> => {
    const baseURL = this.getBaseUrl()

    try {
      return this.api.post('/auth/verification/email', { address: email, client_id: CLIENT_ID }, { baseURL })
    } catch (err) {
      logger().error('Failed to send verification code', { email }, err)
      if (axios.isAxiosError(err)) {
        return err.response
      }
      throw err
    }
  }

  setPassword = async (password: string, token: string): Promise<AxiosResponse | AxiosError['response']> => {
    const baseURL = this.getBaseUrl()

    try {
      const authResponse = await this.api.post(
        '/api/set-password/',
        {
          password,
          password_confirmation: password,
        },
        { baseURL, headers: { Authorization: token } },
      )
      return authResponse
    } catch (err) {
      if (axios.isAxiosError(err)) {
        return err.response
      }
      throw err
    }
  }

  submitVerificationCode = async (email: string, code: string) => {
    const baseURL = this.getBaseUrl()

    return this.api.post('/auth/verification/verify', { address: email, code }, { baseURL })
  }

  requestPasswordReset = async (email: string): Promise<AxiosResponse | undefined> => {
    const baseURL = this.getBaseUrl()

    try {
      const response = await this.api.post(
        '/reset-password-request',
        { email, client_id: CLIENT_ID },
        {
          baseURL,
          headers: { 'Content-Type': 'application/json' },
        },
      )
      return response
    } catch (err) {
      if (axios.isAxiosError(err)) {
        return err.response
      }
      throw err
    }
  }
}

export default new AuthApi()
